import React, { useEffect, useState } from 'react';
import 'styles/style.css';
import { Tooltip, Breadcrumb, Button, Card, Col, Divider, message, notification, Row, Tag, Alert } from 'antd';
import api from '../../../utils/api';
import FourOhFour from '../../../pages/404';
import UIStore from '../../../stores/UIStore';
import {
    AllDynamicQuantities,
    NoQuotableLineItems,
    PartDimensions,
    PartPreview,
    QuotedQuantity,
} from '../../../components/Quotes';
import { Link, useHistory, useParams } from 'react-router-dom';
import { AdditionalRequirementsProps, QuoteLineItemProps, QuoteProps } from '../../../types/types';
import _ from 'lodash';
import QuoteItemInformation from '../../../components/Quotes/QuoteItemInformation';
import { Price, LeadTimePriceOptions, LeadTimeOptionCard, SafeHtml } from '@partsbadger/library';
import { QuoteLineItemProps as QuoteLineItemPropsFromType } from '@partsbadger/types';

import QuoteDetailHeader from '../../../components/Quotes/QuoteDetailHeader';

import PortalLoading from '../../../components/PortalLoading';

import { ThreeDViewerModal, viewerPartInfoFromQuotePart } from '../../../components/ThreeDViewerModal';
import { OrderedListOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import UserStore from '../../../stores/UserStore';
import SalesOrderStore from '../../../stores/SalesOrderStore';
import QuoteStore from '../../../stores/QuoteStore';

import QuoteQuantityInput from '../../../components/Quotes/QuoteQuantityInput';
import dayjs from 'dayjs';

interface IQuoteDetailParams {
    id: string | undefined;
}
interface AlertProps {
    message: string;
    description: React.ReactNode;
    type?: 'error' | 'warning' | 'info' | 'success';
}

const QuoteDetail: React.FC = () => {
    const [quote_line_items, setQuoteLineItems] = useState<Array<QuoteLineItemProps & QuoteLineItemPropsFromType>>([]);

    const history = useHistory();

    const [loading, setLoading] = useState<boolean>(false);
    const [isCreatingADraftOrder, setIsCreatingADraftOrder] = useState<boolean>(false);
    const [quote, setQuote] = useState<QuoteProps | null>(null);
    const [additional_requirements, setAdditionalRequirements] = useState<Array<AdditionalRequirementsProps>>([]);
    const [showAllDynamicQuantitiesByItem, setShowAllDynamicQuantitiesByItem] = useState<number | null>(null);
    const [url, setUrl] = useState<string>('');
    const [isExpired, setIsExpired] = useState<boolean>(false);
    const [lineItemsByPart, setLineItemsByPart] = useState<_.Dictionary<QuoteLineItemProps[]>>();

    const { id } = useParams<IQuoteDetailParams>();

    const getLineItems = async (id: number) => {
        const response = await api.Quotes.lineItems(id);
        response.results.map((item: QuoteLineItemProps) => {
            if (item.has_dynamic_quantities) {
                const dynamics = item.quote_item_quantities.filter(dynamic => dynamic.displayed);
                if (dynamics.length === 0) {
                    response.results.splice(
                        response.results.findIndex((_item: QuoteLineItemProps) => _item.id === item.id),
                        1
                    );
                }
            }

            if (item?.safe_auto_quote && item?.pricing_options?.prices_by_quantity) {
                const hasExpired = Object.values(item.pricing_options.prices_by_quantity).some(quantity =>
                    quantity.some(option => dayjs(option?.expiration_date).isBefore(dayjs()))
                );

                if (hasExpired) {
                    item.expired = true;
                } else {
                    item.expired = false;
                }
            }
        });

        const getLineItemsGroupBy: _.Dictionary<QuoteLineItemProps[]> = _.groupBy(response?.results, item => {
            return item?.product?.master_product;
        });

        setLineItemsByPart(getLineItemsGroupBy);

        setQuoteLineItems(response.results);
    };

    const getQuoteDetails = async (id: any) => {
        const response = await api.Quotes.get(id);
        setQuote(response);
    };

    const getAdditionalRequirements = async (id: number) => {
        const response = await api.Quotes.getAdditionalRequirements(id);
        setAdditionalRequirements(response.results);
    };

    const loadAllData = async (id: number) => {
        setLoading(true);
        await getQuoteDetails(id);
        await getLineItems(id);
        await getAdditionalRequirements(id);
        setLoading(false);
    };

    const handleClickNewRevision = () => {
        if (quote?.id) {
            setIsCreatingADraftOrder(true);

            QuoteStore.createQuoteNewRevision(quote.id)
                .then(data => {
                    message.success(`Revision ${data.name} created`);
                    setIsCreatingADraftOrder(false);
                    history.push(`/my-account/quotes/${data.id}/order/`);
                })
                .catch(() => {
                    message.error('Something went wrong!');
                    setIsCreatingADraftOrder(false);
                });
        }
    };

    useEffect(() => {
        UIStore.setSelectedKey(['quotes']);
        UIStore.setDocumentTitle('Quote Detail');
        if (id) {
            loadAllData(parseInt(id)).catch(() => {
                setLoading(false);
            });
        }
    }, [id]);

    if (!loading && !quote?.id) {
        return <FourOhFour />;
    }

    const links = [
        {
            title: 'Quote List',
            link: '/my-account/quotes/',
        },
        {
            title: quote?.name ?? 'Original Quote',
            link: null,
        },
    ];
    const breadcrumbs = links?.map((breadcrumb, index) => {
        return (
            <Breadcrumb.Item key={breadcrumb.title} href={breadcrumb.link ?? ''}>
                {breadcrumb.title}
            </Breadcrumb.Item>
        );
    });

    const sendToRequote = async (id: number | undefined) => {
        {
            if (id) {
                QuoteStore.requote(id)
                    .then(response => {
                        history.replace(`/my-account/quotes/${response.id}/continue/`);
                    })
                    .catch(error => {
                        message.error('Something went wrong!');
                    });
            }
        }
    };

    function renderAlert({ message, description, type = 'error' }: AlertProps) {
        return (
            <Row>
                <Col span={24}>
                    <Alert type={type} showIcon={true} message={message} description={description} className="mb-3" />
                </Col>
            </Row>
        );
    }

    const canSalesOrderBeCreated = quote?.no_quotable_items.length === quote?.line_items.length;

    const isBeingQuoted = quote?.customer_status == 'In Progress';
    const isCompleted = quote?.customer_status == 'Completed';
    const isCancelled = quote?.customer_status == 'Cancelled';
    const isDraft = quote?.customer_status == 'Draft';
    const canBeOrdered = !isBeingQuoted && !isCancelled && !isDraft;

    const hidePrices = isBeingQuoted || isCancelled || quote?.customer_status == 'Draft';

    return (
        <>
            <QuoteDetailHeader
                quote={quote}
                quote_line_items={quote_line_items}
                editQuote={false}
                quoteExpired={canSalesOrderBeCreated || isExpired}
            />

            {loading ? (
                <PortalLoading />
            ) : (
                <div
                    className="container"
                    data-testid="page-quote-detail"
                    data-quote-id={quote?.id ? String(quote.id) : undefined}
                    data-ready={String(!loading && Boolean(quote?.id))}
                >
                    <Row className="container">
                        <Breadcrumb>{breadcrumbs}</Breadcrumb>
                    </Row>
                    {/* {isAutoGreenIsExpired ||
                        (isExpired &&
                            renderAlert({
                                message: 'Quote has expired',
                                type: 'warning',
                                description: (
                                    <span>
                                        The quote you're viewing has expired items. Generate a new version to ensure
                                        accurate pricing and availability{' '}
                                        <a className="text-blue-600" onClick={() => sendToRequote(quote?.id)}>
                                            Requote now
                                        </a>
                                    </span>
                                ),
                            }))} */}
                    {canSalesOrderBeCreated &&
                        renderAlert({
                            message: 'No Purchasable Items Found in the Quote',
                            description: (
                                <span>
                                    The quote you're attempting to order does not include any valid quoted parts. Please
                                    request a requote or reach out to a sales representative for further assistance{' '}
                                    <a className="text-blue-600" onClick={() => sendToRequote(quote?.id)}>
                                        submit for a re-quote.
                                    </a>
                                </span>
                            ),
                        })}

                    {isBeingQuoted ? (
                        <Row>
                            <Col sm={24}>
                                <Alert
                                    type="warning"
                                    showIcon={true}
                                    description="All parts have been submitted for manual quoting by our Design Engineers.
                                   A detailed quote will be returned shortly"
                                    className="mb-3"
                                />
                            </Col>
                        </Row>
                    ) : null}

                    {isCancelled ? (
                        <Row>
                            <Col sm={24}>
                                <Alert
                                    type="warning"
                                    showIcon={true}
                                    description={
                                        <div>
                                            The quote has been canceled. If you need to modify or add products, you can
                                            request a requote.
                                        </div>
                                    }
                                    className="mb-3"
                                />
                            </Col>
                        </Row>
                    ) : null}

                    {isDraft ? (
                        <Row>
                            <Col sm={24}>
                                <Alert
                                    type="info"
                                    showIcon={true}
                                    description={
                                        <div>
                                            <p>
                                                The quote is in draft status. You can continue working on it by clicking
                                                the link below.
                                            </p>
                                            <Button
                                                type="primary"
                                                onClick={() => history.push(`/my-account/quotes/${id}/continue/`)}
                                            >
                                                Continue Quote
                                            </Button>
                                        </div>
                                    }
                                    className="mb-3"
                                />
                            </Col>
                        </Row>
                    ) : null}

                    <Row gutter={[16, 8]}>
                        {(quote_line_items.length > 0 || (quote?.no_quotable_items?.length ?? 0 > 0)) && (
                            <>
                                <Col xl={16} span={24}>
                                    {_.map(
                                        lineItemsByPart,
                                        (
                                            _line_items: QuoteLineItemPropsFromType[] & QuoteLineItemProps[],
                                            key: number
                                        ) => {
                                            const first_part = _line_items[0];

                                            return (
                                                <Card
                                                    key={first_part.uid ?? `part-${key}`}
                                                    className="shadow-sm mb-3"
                                                    bodyStyle={{ paddingTop: '0.5rem' }}
                                                >
                                                    <div className="d-flex flex-col xl:flex-row xl:align-items-center mb-1">
                                                        <div className="mr-4">
                                                            {first_part.product.attachments.file3d?.file_3djs ? (
                                                                <ThreeDViewerModal
                                                                    url={
                                                                        first_part.product.attachments.file3d.file_3djs
                                                                    }
                                                                    partInfo={viewerPartInfoFromQuotePart(first_part)}
                                                                >
                                                                    <PartPreview
                                                                        thumbnail={
                                                                            first_part.product?.attachments?.file3d
                                                                                ?.image
                                                                        }
                                                                        has_file_3d={first_part?.product?.has_file_3d}
                                                                        width="85px"
                                                                    />
                                                                </ThreeDViewerModal>
                                                            ) : (
                                                                <PartPreview
                                                                    thumbnail={
                                                                        first_part.product?.attachments?.file3d?.image
                                                                    }
                                                                    has_file_3d={first_part?.product?.has_file_3d}
                                                                    width="85px"
                                                                />
                                                            )}
                                                        </div>

                                                        <div className={'part-information'}>
                                                            <div className="font-size-md font-weight-bold w-100">
                                                                {first_part.name}
                                                                {first_part.product.itar ? (
                                                                    <Tag
                                                                        color="#9fbf91"
                                                                        style={{ fontSize: '0.75em' }}
                                                                        className="ml-2"
                                                                    >
                                                                        ITAR
                                                                    </Tag>
                                                                ) : null}

                                                                <div>
                                                                    <PartDimensions
                                                                        length={
                                                                            first_part.product.dimensions_dict.length
                                                                        }
                                                                        width={first_part.product.dimensions_dict.width}
                                                                        height={
                                                                            first_part.product.dimensions_dict.height
                                                                        }
                                                                        unit={first_part.product.dimensions_dict.unit}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {first_part.product.deviation_note && (
                                                                <div
                                                                    className={'italic'}
                                                                    style={{
                                                                        width: '300px',
                                                                    }}
                                                                >
                                                                    {first_part.product.deviation_note}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {first_part?.safe_auto_quote && first_part?.expired && (
                                                            <div className="self-end ml-auto">
                                                                <span>
                                                                    Some options have expired.{' '}
                                                                    <a
                                                                        // className="text-blue-600"
                                                                        style={{
                                                                            color: '#c00',
                                                                        }}
                                                                        onClick={() => handleClickNewRevision()}
                                                                    >
                                                                        Click here to see updated prices.
                                                                    </a>
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div key={key} className="d-flex flex-row w-100">
                                                        <div className="d-flex flex-column w-100">
                                                            {_line_items.map((_item, index) => {
                                                                return (
                                                                    <>
                                                                        <div
                                                                            key={_item.id}
                                                                            className="d-flex flex-md-row flex-column justify-between"
                                                                        >
                                                                            <div className="mb-3">
                                                                                <QuoteItemInformation
                                                                                    manufacturing_type={
                                                                                        _item.manufacturing_type
                                                                                    }
                                                                                    material_name={
                                                                                        _item.product.material
                                                                                    }
                                                                                    custom_material={
                                                                                        _item.product.custom_material
                                                                                    }
                                                                                    finish_name={
                                                                                        _item.product.finish_related
                                                                                            ?.name
                                                                                    }
                                                                                    custom_finish={
                                                                                        _item.product.custom_finish ||
                                                                                        ''
                                                                                    }
                                                                                    coating_name={
                                                                                        _item.product.coating_related
                                                                                            ?.name
                                                                                    }
                                                                                    custom_coating={
                                                                                        _item.product.custom_coating ||
                                                                                        ''
                                                                                    }
                                                                                />
                                                                            </div>

                                                                            <div className="flex-grow-1 mb-3">
                                                                                {_item.has_dynamic_quantities ? (
                                                                                    _item.quote_item_quantities
                                                                                        .filter(item => item.displayed)
                                                                                        .map(
                                                                                            (
                                                                                                _quantity_item,
                                                                                                _quantity_item_index
                                                                                            ) => {
                                                                                                return (
                                                                                                    <div
                                                                                                        key={
                                                                                                            _quantity_item.quantity
                                                                                                        }
                                                                                                    >
                                                                                                        <QuotedQuantity
                                                                                                            showTitles={
                                                                                                                _quantity_item_index ==
                                                                                                                0
                                                                                                            }
                                                                                                            quantity={
                                                                                                                _quantity_item.quantity
                                                                                                            }
                                                                                                            unit_price={parseFloat(
                                                                                                                _quantity_item.unit_price
                                                                                                            )}
                                                                                                            total_price={parseFloat(
                                                                                                                _quantity_item.total_price
                                                                                                            )}
                                                                                                            lead_time={
                                                                                                                _quantity_item.lead_time
                                                                                                            }
                                                                                                            max_quantity={
                                                                                                                _item.quantity_max
                                                                                                            }
                                                                                                            editable={
                                                                                                                false
                                                                                                            }
                                                                                                            hidePrices={
                                                                                                                hidePrices
                                                                                                            }
                                                                                                        />
                                                                                                    </div>
                                                                                                );
                                                                                            }
                                                                                        )
                                                                                ) : (
                                                                                    <div
                                                                                        key={_item.id}
                                                                                        className="d-flex"
                                                                                    >
                                                                                        <div className="flex flex-col justify-center items-end ml-auto">
                                                                                            <QuoteQuantityInput
                                                                                                quantity={
                                                                                                    _item.quantity
                                                                                                }
                                                                                                max_quantity={
                                                                                                    _item.quantity_max
                                                                                                }
                                                                                                editable={false}
                                                                                            />

                                                                                            {!hidePrices ? (
                                                                                                !_item.safe_auto_quote ? (
                                                                                                    <div>
                                                                                                        <LeadTimeOptionCard
                                                                                                            option={{
                                                                                                                id:
                                                                                                                    'price',
                                                                                                                lead_time:
                                                                                                                    _item.lead_time,
                                                                                                                unit_price: parseFloat(
                                                                                                                    _item.unit_price
                                                                                                                ),
                                                                                                                safe_auto_quote: false,
                                                                                                                total_price: parseFloat(
                                                                                                                    _item?.total_price
                                                                                                                ),
                                                                                                                quantity:
                                                                                                                    _item.quantity,
                                                                                                                expiration_date:
                                                                                                                    '',
                                                                                                            }}
                                                                                                            readonly={
                                                                                                                true
                                                                                                            }
                                                                                                            lineItem={
                                                                                                                _item
                                                                                                            }
                                                                                                            isDisabled={
                                                                                                                false
                                                                                                            }
                                                                                                        />
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    _item?.pricing_options && (
                                                                                                        <>
                                                                                                            {/* <div className="flex flex-row items-center mb-2 mt-2">
                                                                                                                <Tooltip
                                                                                                                    title={
                                                                                                                        'You can select the price during checkout'
                                                                                                                    }
                                                                                                                >
                                                                                                                    <QuestionCircleOutlined
                                                                                                                        className=""
                                                                                                                        style={{
                                                                                                                            cursor:
                                                                                                                                'pointer',
                                                                                                                            fontSize:
                                                                                                                                '14px',
                                                                                                                            color:
                                                                                                                                '#D92923',
                                                                                                                        }}
                                                                                                                    />
                                                                                                                </Tooltip>
                                                                                                            </div> */}
                                                                                                            <LeadTimePriceOptions
                                                                                                                lineItem={
                                                                                                                    _item
                                                                                                                }
                                                                                                            />
                                                                                                        </>
                                                                                                    )
                                                                                                )
                                                                                            ) : null}
                                                                                        </div>
                                                                                        {/* <QuotedQuantity
                                                                                        showTitles
                                                                                        quantity={_item.quantity}
                                                                                        unit_price={parseFloat(
                                                                                            _item.unit_price
                                                                                        )}
                                                                                        total_price={parseFloat(
                                                                                            _item.total_price
                                                                                        )}
                                                                                        lead_time={_item.lead_time}
                                                                                        max_quantity={
                                                                                            _item.quantity_max
                                                                                        }
                                                                                        editable={false}
                                                                                        hidePrices={hidePrices}
                                                                                    /> */}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        {index !== _line_items.length - 1 ? (
                                                                            <Divider
                                                                                type={'horizontal'}
                                                                                className="m-0 p-0 mb-3"
                                                                            />
                                                                        ) : null}
                                                                    </>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </Card>
                                            );
                                        }
                                    )}

                                    {quote && quote?.no_quotable_items.length > 0 ? (
                                        <>
                                            <Col span={24}>
                                                <div className={'m-4'}>
                                                    <OrderedListOutlined /> NO QUOTED ITEMS
                                                </div>

                                                <Card
                                                    style={{
                                                        width: '100%',
                                                    }}
                                                >
                                                    {quote && (
                                                        <NoQuotableLineItems
                                                            no_quotable_items={quote.no_quotable_items}
                                                        />
                                                    )}
                                                </Card>
                                            </Col>
                                        </>
                                    ) : null}
                                </Col>
                                <Col xl={8} span={24}>
                                    <Card>
                                        {!isBeingQuoted && quote?.notes && (
                                            <>
                                                <p className={'font-bold font-xl'}>Quoting Notes:</p>
                                                <SafeHtml html={quote?.notes} />
                                            </>
                                        )}
                                        <div>
                                            <p className="font-bold my-2 text-xl">Quote includes:</p>
                                            <div className="flex flex-wrap gap-2 mb-1">
                                                {quote?.custom_requirements?.map(i => (
                                                    <Tag key={i.key}>{i.label}</Tag>
                                                ))}
                                            </div>
                                        </div>
                                        {quote?.application_cost_items?.length &&
                                        quote?.application_cost_items?.length > 0 ? (
                                            <>
                                                <Divider />
                                                <div>
                                                    <p className="font-bold mb-2 font-xl">
                                                        Additional Costs:
                                                    </p>
                                                    {quote?.application_cost_items.map(i => {
                                                        return (
                                                            <div
                                                                className={
                                                                    'application-item flex flex-row justify-between  mb-1'
                                                                }
                                                                key={i.id}
                                                            >
                                                                <div className={'application-name'}>
                                                                    {i.additional_requirement}
                                                                </div>
                                                                <div>
                                                                    {hidePrices ? 'Quoting' : <Price value={i.price} />}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                <Divider />
                                            </>
                                        ) : null}
                                        <div
                                            className={'flex flex-col justify-between mt-2'}
                                            style={{ fontSize: '16px' }}
                                        >
                                            <div className="flex flex-row justify-between  mb-1">
                                                <div className="summary-label">Subtotal</div>
                                                <div className="summary-value">
                                                    {hidePrices ? 'Pending' : <Price value={quote?.total_price ?? 0} />}
                                                </div>
                                            </div>

                                            <div className="flex flex-row justify-between  mb-1">
                                                <div className="summary-label">Application Cost:</div>
                                                <div className="summary-value">
                                                    {hidePrices ? (
                                                        'Pending'
                                                    ) : (
                                                        <Price value={quote?.application_cost ?? 0} />
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex flex-row justify-between mt-1">
                                                <div className="summary-label">Discount</div>
                                                <div className="summary-value">
                                                    {hidePrices ? 'Pending' : <Price value={quote?.discount ?? 0} />}
                                                </div>
                                            </div>

                                            <div className="flex flex-row justify-between  mt-2">
                                                <div className="summary-label">
                                                    <div className={'font-bold'} style={{ fontSize: '16px' }}>
                                                        Estimated Sales Tax:
                                                    </div>
                                                    <div className={'text-xs'}>
                                                        {hidePrices ? (
                                                            ''
                                                        ) : (
                                                            <>
                                                                (Zip Code: {quote?.shipping_zip_code}; Percentage:{' '}
                                                                {quote?.taxes_rate}%)
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="summary-value">
                                                    {hidePrices ? 'Pending' : <Price value={quote?.taxes ?? 0} />}
                                                </div>
                                            </div>

                                            <div
                                                className="flex flex-row justify-between  mt-2 pt-4"
                                                style={{
                                                    borderTop: 'solid 1px #d9d9d9',
                                                }}
                                            >
                                                <div className="font-bold text-xl" style={{ fontSize: '20px' }}>
                                                    Grand Total
                                                </div>
                                                <div className="text-xl">
                                                    {hidePrices ? 'Pending' : <Price value={quote?.grand_total ?? 0} />}
                                                </div>
                                            </div>

                                            {!isExpired && !isBeingQuoted && !isCancelled && canBeOrdered ? (
                                                <div className={'flex flex-row justify-end mt-4'}>
                                                    <Button
                                                        onClick={handleClickNewRevision}
                                                        type="primary"
                                                        size="large"
                                                        className="w-full"
                                                        loading={isCreatingADraftOrder}
                                                        disabled={canSalesOrderBeCreated || isExpired || !canBeOrdered}
                                                    >
                                                        Continue to Checkout
                                                    </Button>
                                                </div>
                                            ) : null}
                                        </div>
                                    </Card>
                                </Col>
                            </>
                        )}
                    </Row>

                    {quote && showAllDynamicQuantitiesByItem && (
                        <AllDynamicQuantities
                            quote_id={quote.id}
                            item_id={showAllDynamicQuantitiesByItem}
                            handleClosePrices={() => setShowAllDynamicQuantitiesByItem(null)}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default QuoteDetail;
